import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Stack,
  Avatar,
  Paper,
  Chip,
  Button,
  Slide,
  IconButton,
} from "@mui/material";
import {
  CelebrationOutlined,
  AttachMoney,
  CheckCircle,
  Close,
  AccountBalanceWallet,
  TrendingUp,
} from "@mui/icons-material";
import { green, amber } from "@mui/material/colors";
import type { TransitionProps } from "@mui/material/transitions";
import React, { useEffect, useState } from "react";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Confetti component
const ConfettiPiece = ({
  delay,
  duration,
  left,
}: {
  delay: number;
  duration: number;
  left: number;
}) => (
  <Box
    sx={{
      position: "absolute",
      top: -10,
      left: `${left}%`,
      width: 8,
      height: 8,
      backgroundColor: [
        amber[400],
        green[400],
        "#ff6b6b",
        "#4ecdc4",
        "#45b7d1",
      ][Math.floor(Math.random() * 5)],
      animation: `confetti-fall ${duration}s ${delay}s ease-in-out infinite`,
      "@keyframes confetti-fall": {
        "0%": {
          transform: "translateY(-100vh) rotate(0deg)",
          opacity: 1,
        },
        "100%": {
          transform: "translateY(100vh) rotate(720deg)",
          opacity: 0,
        },
      },
    }}
  />
);

const Confetti = () => {
  const pieces = Array.from({ length: 50 }, (_, i) => (
    <ConfettiPiece
      key={i}
      delay={Math.random() * 3}
      duration={3 + Math.random() * 2}
      left={Math.random() * 100}
    />
  ));

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 9999,
        overflow: "hidden",
      }}
    >
      {pieces}
    </Box>
  );
};

interface WithdrawalPopupProps {
  open: boolean;
  onClose: () => void;
  response?: string;
  amount: number;
  currency: string;
}

export default function WithdrawalPopup({
  open,
  onClose,
  response = "success",
  amount,
  currency,
}: WithdrawalPopupProps) {
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (open) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [open]);

  return (
    <>
      {showConfetti && <Confetti />}
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
            overflow: "visible",
          },
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "grey.500",
            zIndex: 1,
          }}
        >
          <Close />
        </IconButton>

        <DialogContent sx={{ textAlign: "center", p: 4 }}>
          {/* Celebration Icon */}
          <Box sx={{ position: "relative", mb: 3 }}>
            <Avatar
              sx={{
                bgcolor: amber[100],
                width: 80,
                height: 80,
                mx: "auto",
                mb: 2,
                animation: "bounce 2s ease-in-out infinite",
                "@keyframes bounce": {
                  "0%, 20%, 50%, 80%, 100%": {
                    transform: "translateY(0)",
                  },
                  "40%": {
                    transform: "translateY(-10px)",
                  },
                  "60%": {
                    transform: "translateY(-5px)",
                  },
                },
              }}
            >
              <CelebrationOutlined sx={{ fontSize: 40, color: amber[600] }} />
            </Avatar>
          </Box>

          {/* Congratulations Message */}
          <Typography
            variant="h4"
            component="h1"
            fontWeight="bold"
            gutterBottom
          >
            Congratulations!
          </Typography>

          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            You've successfully collected
          </Typography>

          {/* Amount Section */}
          <Paper
            elevation={0}
            sx={{
              bgcolor: green[50],
              border: `2px solid ${green[200]}`,
              borderRadius: 3,
              p: 4,
              mb: 3,
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: `linear-gradient(90deg, ${green[400]}, ${amber[400]}, ${green[400]})`,
                animation: "shimmer 2s ease-in-out infinite",
                "@keyframes shimmer": {
                  "0%": { transform: "translateX(-100%)" },
                  "100%": { transform: "translateX(100%)" },
                },
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <AttachMoney sx={{ color: green[600], fontSize: 32, mr: 1 }} />
              <Typography
                variant="h2"
                component="div"
                fontWeight="bold"
                color={green[700]}
              >
                ${(amount / 100).toFixed(2)} {currency}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} justifyContent="center">
              <Chip
                icon={<CheckCircle sx={{ fontSize: 16 }} />}
                label="Withdrawal Successful"
                sx={{
                  bgcolor: green[100],
                  color: green[800],
                  fontWeight: "medium",
                  "& .MuiChip-icon": {
                    color: green[600],
                  },
                }}
              />
              <Chip
                icon={<TrendingUp sx={{ fontSize: 16 }} />}
                label="Funds Available"
                sx={{
                  bgcolor: amber[100],
                  color: amber[800],
                  fontWeight: "medium",
                  "& .MuiChip-icon": {
                    color: amber[600],
                  },
                }}
              />
            </Stack>
          </Paper>

          {/* Success Details */}
          <Box
            sx={{
              bgcolor: "grey.50",
              borderRadius: 2,
              p: 3,
              mb: 3,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{ mb: 1 }}
            >
              <AccountBalanceWallet sx={{ color: "grey.600", fontSize: 20 }} />
              <Typography
                variant="body2"
                fontWeight="medium"
                color="text.secondary"
              >
                Transaction Status
              </Typography>
            </Stack>
            <Typography variant="body1" fontWeight="bold" color="text.primary">
              {response.charAt(0).toUpperCase() + response.slice(1)}
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              onClick={onClose}
              sx={{
                bgcolor: green[600],
                "&:hover": { bgcolor: green[700] },
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontWeight: "bold",
              }}
            >
              Awesome!
            </Button>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                borderColor: green[600],
                color: green[600],
                "&:hover": {
                  borderColor: green[700],
                  bgcolor: green[50],
                },
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontWeight: "bold",
              }}
            >
              View Balance
            </Button>
          </Stack>

          {/* Footer Message */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 3, display: "block" }}
          >
            Your funds have been successfully transferred to your account
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
}
